import { ARENA_IDS, ARENA_NAMES, arenaForSeed, type ArenaId } from '@steelwake/shared/arena';
import type { BotLevel } from '@steelwake/shared/combat';
import { MECH_LIST } from '@steelwake/shared/data';
import { Button, colors, spacing } from '@steelwake/ui';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { BOT_LEVELS, freshSeed, matchSearch, opponentForSeed } from '../../game/match/setup.js';
import { MechPicker } from './MechPicker.js';

/** The practice lobby: pick a mech, an arena, an opponent and a difficulty, then deploy. */
export function MechSelect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mech, setMech] = useState<string>('warden');
  const [arena, setArena] = useState<ArenaId | 'random'>('random');
  const [opponent, setOpponent] = useState<string>('random');
  const [bot, setBot] = useState<BotLevel>('normal');

  const deploy = () => {
    const seed = freshSeed();
    const params = {
      arena: arena === 'random' ? arenaForSeed(seed) : arena,
      mech,
      opponent: opponent === 'random' ? opponentForSeed(seed, mech) : opponent,
      bot,
      seed,
      roundSeconds: 180,
      countdownSeconds: 10,
      roundsToWin: 2,
      ultReady: false,
    };
    void navigate(`/play/match${matchSearch(params, location.search)}`);
  };

  const selectStyle = { display: 'block', width: '100%', marginTop: 4 } as const;

  return (
    <MechPicker
      testId="mech-select"
      value={mech}
      onChange={setMech}
      title="Practice · choose your mech"
      intro="All fifteen frames are open in practice. Practice pays nothing; unlocks and rewards arrive with online play."
      footer={
        <>
          <label style={{ display: 'block', fontSize: 13, marginBottom: spacing.sm }}>
            <span style={{ color: colors.textMuted }}>Arena</span>
            <select
              data-testid="arena-select"
              value={arena}
              onChange={(e) => setArena(e.target.value as ArenaId | 'random')}
              style={selectStyle}
            >
              <option value="random">Random</option>
              {ARENA_IDS.map((id) => (
                <option key={id} value={id}>
                  {ARENA_NAMES[id]}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'block', fontSize: 13, marginBottom: spacing.sm }}>
            <span style={{ color: colors.textMuted }}>Opponent</span>
            <select
              data-testid="opponent-select"
              value={opponent}
              onChange={(e) => setOpponent(e.target.value)}
              style={selectStyle}
            >
              <option value="random">Random</option>
              {MECH_LIST.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4 }}>
            Bot difficulty
          </div>
          <div style={{ display: 'flex', gap: spacing.sm, marginBottom: spacing.lg }}>
            {BOT_LEVELS.filter((l) => l !== 'idle').map((level) => (
              <Button
                key={level}
                variant={bot === level ? 'primary' : 'ghost'}
                aria-pressed={bot === level}
                data-testid={`difficulty-${level}`}
                onClick={() => setBot(level)}
              >
                {level}
              </Button>
            ))}
          </div>
          <Button data-testid="deploy" onClick={deploy}>
            Deploy
          </Button>
        </>
      }
    />
  );
}
